// src/pages/TicketLookup/index.tsx
// Trang tra cứu kết quả vé số công khai — mở từ link/QR in trên vé, không cần đăng nhập.
// Route: /v/:token HOẶC /:token (token là chuỗi base64url mã hóa AES-256-GCM ký quay/số vé/ký
// hiệu, xem QrTokenCodec bên ticket-service) — thay cho định dạng cũ
// /v/:periodId/:ticketNumber/:symbol?sig=... Route "/:token" tồn tại vì LookupBaseUrl cấu hình ở
// System Configuration có thể KHÔNG chứa đoạn "/v" (VD: "http://localhost:3000/"), lúc đó URL/QR
// thực tế in trên vé là "{lookupBaseUrl}/{token}" chứ không có "/v".
// Thiết kế dạng trang đầy đủ (không phải popup nhỏ giữa màn hình).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';

import { lookupTicket } from 'helpers/fakebackend_helper';
import { DecodedTicketToken, verifyAndDecodeTicketToken } from 'helpers/ticketTokenVerifier';
import { fetchDrawResultFile, isTicketMatchPrize } from 'helpers/drawResultLookup';
import { LOOKUP_ERROR_MESSAGES, TicketLookupResponse, WonPrize } from './types';
import QrScannerModal from './components/QrScannerModal';
import './ticket-lookup.scss';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_lookup');

// Nhận diện link tra cứu từ nội dung QR — hỗ trợ cả "/v/{token}" lẫn "/{token}" (tùy LookupBaseUrl
// đã cấu hình có đoạn "/v" hay không, xem ghi chú route ở trên), và định dạng hash-route cũ đã in
// trên một số vé trước đây: .../#/v/{token}. Việc chấp nhận cả path 1 đoạn không có nghĩa mọi link
// đều được coi hợp lệ — router (allRoutes.tsx) vẫn ưu tiên khớp đúng các route tĩnh khác (VD:
// /login) trước khi rơi vào "/:token", và token sai sẽ bị backend từ chối, hiển thị lỗi bình thường.
const parseLookupTarget = (raw: string): string | null => {
  try {
    const parsed = new URL(raw);
    let target = `${parsed.pathname}${parsed.search}`;

    if (parsed.hash && parsed.hash.includes('/v/')) {
      const hashContent = parsed.hash.replace(/^#/, '');
      target = hashContent.startsWith('/') ? hashContent : `/${hashContent}`;
    }

    return /^\/(?:v\/)?[^/?]+/.test(target) ? target : null;
  } catch {
    return null;
  }
};

type ViewState = 'loading' | 'invalidLink' | 'error' | 'result';

const formatCurrency = (value?: number | null) =>
  st('value_d_text', { value: Number(value || 0).toLocaleString('vi-VN') });

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY') : '—';

const TicketLookupPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [state, setState] = useState<ViewState>('loading');
  const [data, setData] = useState<TicketLookupResponse | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Voi token JWT (ky bang private key ben ticket-service, xem GenerateTicketSignatureQuery), FE tu
  // xac thuc + doc claim bang public key roi doc thang file ket qua cong khai tren MinIO va cham
  // ve — hoan toan khong goi API backend nao (giong het cach LookupPortal check trung thuong).
  const runClientSideLookup = useCallback(async (decoded: DecodedTicketToken) => {
    try {
      const drawResult = await fetchDrawResultFile(decoded.lotteryPeriodId);
      const hasResult = !!drawResult && Number(drawResult.issuancePlanId) === Number(decoded.lotteryPeriodId);

      const matchedPrizes = hasResult
        ? drawResult!.prizes.filter((prize) => isTicketMatchPrize(decoded.ticketNumber, prize))
        : [];
      const wonPrizes: WonPrize[] = matchedPrizes.map((prize) => ({
        prizeLevel: prize.displayName,
        prizeAmount: prize.valuePerTicket,
        winningNumber: prize.winningNumbers.find((w) => decoded.ticketNumber.endsWith(w)) || '',
      }));

      setData({
        success: true,
        ticket: {
          id: 0,
          lotteryPeriodId: decoded.lotteryPeriodId,
          ticketNumber: decoded.ticketNumber,
          symbol: decoded.symbol,
        },
        plan: {
          planCode: decoded.planCode,
          expectedDrawDate: decoded.expectedDrawDate,
        },
        result: {
          hasResult,
          isWinning: wonPrizes.length > 0,
          drawStatus: hasResult ? 'completed' : 'pending',
          actualDrawDate: hasResult ? drawResult!.drawDate : null,
          wonPrizes,
        },
        timestamp: new Date().toISOString(),
        requestId: '',
      });
      setState('result');
    } catch {
      setErrorCode('INTERNAL_ERROR');
      setErrorMessage(null);
      setState('error');
    }
  }, []);

  const runLookup = useCallback(async () => {
    if (!token) {
      setState('invalidLink');
      return;
    }

    setState('loading');

    // Uu tien xac thuc client-side bang public key (token JWT, sinh tu man Cong tra cuu ket qua) —
    // chi fallback goi API cu khi token khong phai JWT (QR in tren ve that, van ma hoa AES-256-GCM).
    const decoded = await verifyAndDecodeTicketToken(token);
    if (decoded) {
      await runClientSideLookup(decoded);
      return;
    }

    try {
      const response: any = await lookupTicket(token);
      const payload: TicketLookupResponse = response?.data || response;

      if (payload?.success) {
        setData(payload);
        setState('result');
      } else {
        setErrorCode(payload?.errorCode || null);
        setErrorMessage(payload?.errorMessage || null);
        setState('error');
      }
    } catch (err: any) {
      const payload = err?.data;
      setErrorCode(payload?.errorCode || null);
      setErrorMessage(payload?.errorMessage || err?.message || null);
      setState('error');
    }
  }, [token, runClientSideLookup]);

  useEffect(() => {
    runLookup();
  }, [runLookup]);

  const handleScanned = useCallback((decodedText: string) => {
    setScannerOpen(false);

    const target = parseLookupTarget(decodedText);
    if (!target) {
      toast.error(st('ma_qr_khong_hop_le_vui_message'));
      return;
    }

    navigate(target);
  }, [navigate]);

  const result = data?.result;
  const isPending = state === 'result' && result && !result.hasResult;
  const isWinning = state === 'result' && result?.hasResult && result.isWinning;
  const isNotWinning = state === 'result' && result?.hasResult && !result.isWinning;

  const themeClass =
    isWinning ? 'is-win' :
    isPending ? 'is-pending' :
    isNotWinning ? 'is-lose' :
    'is-neutral';

  const prizes = isWinning
    ? (result?.wonPrizes && result.wonPrizes.length > 0
        ? result.wonPrizes
        : [{ prizeLevel: result?.prizeLevel || '', prizeAmount: result?.prizeAmount || 0, winningNumber: result?.winningNumber || '' }])
    : [];
  const totalPrizeAmount = prizes.reduce((sum, p) => sum + (p.prizeAmount || 0), 0);

  return (
    <div className={`ticket-lookup-page ${themeClass}`}>
      {/* Thanh header trên cùng */}
      <header className="tlk-topbar">
        <div className="tlk-topbar-inner">
          <div className="tlk-brand">
            <i className="ri-ticket-2-fill"></i>
            <span>{st('tra_cuu_ket_qua_ve_so_text')}</span>
          </div>
          <button
            type="button"
            className="tlk-scan-icon-btn"
            title={st('quet_ma_qr_ve_khac_title')}
            onClick={() => setScannerOpen(true)}
          >
            <i className="ri-qr-scan-2-line"></i>
          </button>
        </div>
      </header>

      <main className="tlk-main">
        {state === 'loading' && (
          <div className="tlk-status">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{st('dang_tai_text')}</span>
            </div>
            <p className="mt-3 mb-0">{st('dang_tra_cuu_thong_tin_ve_text')}</p>
          </div>
        )}

        {state === 'invalidLink' && (
          <div className="tlk-status">
            <i className="ri-link-unlink-m tlk-status-icon text-muted"></i>
            <h5 className="mt-3">{st('duong_dan_khong_hop_le_title')}</h5>
            <p className="text-muted mb-4">
              {st('lien_ket_tra_cuu_thieu_thong_text')}
            </p>
            <button type="button" className="btn btn-primary" onClick={() => setScannerOpen(true)}>
              <i className="ri-qr-scan-2-line align-bottom me-1"></i>
              {st('quet_ma_qr_button')}
            </button>
          </div>
        )}

        {state === 'error' && (
          <div className="tlk-status">
            <i className="ri-error-warning-line tlk-status-icon text-danger"></i>
            <h5 className="mt-3">{st('khong_the_tra_cuu_ve_title')}</h5>
            <p className="text-muted mb-4">
              {(errorCode && LOOKUP_ERROR_MESSAGES[errorCode]) || errorMessage || st('co_loi_xay_ra_vui_long_text')}
            </p>
            <div className="d-flex gap-2 justify-content-center flex-wrap">
              {(!errorCode || errorCode === 'INTERNAL_ERROR') && (
                <button type="button" className="btn btn-outline-primary" onClick={runLookup}>
                  <i className="ri-refresh-line align-bottom me-1"></i>
                  {st('thu_lai_button')}
                </button>
              )}
              <button type="button" className="btn btn-primary" onClick={() => setScannerOpen(true)}>
                <i className="ri-qr-scan-2-line align-bottom me-1"></i>
                {st('quet_ve_khac_button')}
              </button>
            </div>
          </div>
        )}

        {state === 'result' && data && (
          <>
            {/* Hero kết quả — điểm nhấn chính của trang */}
            <section className="tlk-hero">
              {isPending && (
                <>
                  <i className="ri-time-line tlk-hero-icon"></i>
                  <h1 style={{ color: 'white' }}>{st('chua_mo_thuong_title')}</h1>
                  <p>{st('ky_quay_nay_chua_co_ket_text')}</p>
                </>
              )}

              {isWinning && (
                <>
                  <i className="ri-trophy-fill tlk-hero-icon"></i>
                  <h1 style={{ color: 'white' }}>{st('chuc_mung_ve_da_trung_thuong_title')}</h1>
                  <p>{st('chi_tiet_giai_thuong_ben_duoi_text')}</p>
                </>
              )}

              {isNotWinning && (
                <>
                  <i className="ri-emotion-normal-line tlk-hero-icon"></i>
                  <h1>{st('ve_khong_trung_thuong_title')}</h1>
                  <p>{st('chuc_ban_may_man_lan_sau_text')}</p>
                </>
              )}
            </section>

            <div className="tlk-content">
              {/* Thông tin vé dạng thẻ vé số */}
              <section className="tlk-ticket-card">
                <div className="tlk-ticket-card-top">
                  <div>
                    <span className="tlk-label">{st('so_ve_text')}</span>
                    <div className="tlk-ticket-number">{data.ticket?.ticketNumber}</div>
                  </div>
                  <div className="tlk-symbol-badge">{data.ticket?.symbol}</div>
                </div>
                <div className="tlk-ticket-divider"></div>
                <div className="tlk-ticket-grid">
                  {data.plan?.planCode && (
                    <div className="tlk-ticket-field">
                      <span className="tlk-label">{st('ky_phat_hanh_text')}</span>
                      <strong>{data.plan.planCode}</strong>
                    </div>
                  )}
                  <div className="tlk-ticket-field">
                    <span className="tlk-label">{result?.hasResult ? st('ngay_mo_thuong_text') : st('du_kien_mo_thuong_text')}</span>
                    <strong>
                      {result?.hasResult
                        ? formatDate(result?.actualDrawDate)
                        : formatDate(data.plan?.expectedDrawDate)}
                    </strong>
                  </div>
                </div>
              </section>

              {/* Chi tiết giải thưởng */}
              {isWinning && (
                <section className="tlk-prizes">
                  <h6 className="tlk-section-title">{st('giai_thuong_title')}</h6>
                  <div className="tlk-prize-list">
                    {prizes.map((prize, idx) => (
                      <div className="tlk-prize-row" key={idx}>
                        <div className="tlk-prize-badge">
                          <i className="ri-award-fill"></i>
                        </div>
                        <div className="tlk-prize-name">{prize.prizeLevel}</div>
                        <div className="tlk-prize-amount">{formatCurrency(prize.prizeAmount)}</div>
                      </div>
                    ))}
                  </div>
                  {prizes.length > 1 && (
                    <div className="tlk-prize-total">
                      <span>{st('tong_tien_trung_thuong_text')}</span>
                      <strong>{formatCurrency(totalPrizeAmount)}</strong>
                    </div>
                  )}
                </section>
              )}

              {/* Kêu gọi hành động */}
              <section className="tlk-cta">
                <button
                  type="button"
                  className="btn btn-primary btn-lg w-100"
                  onClick={() => setScannerOpen(true)}
                >
                  <i className="ri-qr-scan-2-line align-bottom me-2"></i>
                  {st('quet_ma_qr_ve_khac_title')}
                </button>
              </section>
            </div>
          </>
        )}
      </main>

      <footer className="tlk-footer">
        {st('he_thong_tra_cuu_ket_qua_text')}
      </footer>

      <QrScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScanned={handleScanned}
      />
    </div>
  );
};

export default TicketLookupPage;
